import React, { useState, useRef, useEffect } from 'react';
import LearnMessage from './LearnMessage';
import { learnApi } from '../../api/learn';
import { useZoom } from '../Common';
import { TextSizeStepper } from '../../components/Common/TextSizeStepper';
import { RESEARCH_CHAT_TEXT_SIZE } from '../../utils/textSizeRange';

/**
 * LearnChatTab - Chat interface for Learn conversations.
 *
 * Features:
 * - Message list with auto-scroll
 * - Input area for sending messages
 * - Bookmark action on assistant messages (pins directly to uncategorized)
 * - Loading state while waiting for response
 */
export default function LearnChatTab({
  conversationId,
  messages,
  onMessageSent,
  onBookmarkCreated
}) {
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [bookmarkingMessageId, setBookmarkingMessageId] = useState(null);
  const [zoom, setZoom] = useZoom('brainstormy.panel.learn.zoom');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async () => {
    const content = inputValue.trim();
    if (!content || isSending || !conversationId) return;

    setInputValue('');
    setIsSending(true);
    setError(null);

    try {
      const response = await learnApi.sendMessage(conversationId, content);

      if (response?.data) {
        onMessageSent(response.data.user_message, response.data.assistant_message);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again.');
      // Restore input value on error
      setInputValue(content);
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle pinning a message to Library (directly to uncategorized)
  const handleBookmark = async (messageId) => {
    if (bookmarkingMessageId) return; // Prevent double-clicks

    setBookmarkingMessageId(messageId);
    try {
      const response = await learnApi.createBookmark(messageId, null);
      if (response?.data) {
        onBookmarkCreated(messageId, response.data.id);
      }
    } catch (err) {
      console.error('Failed to create bookmark:', err);
      setError('Failed to pin message. Please try again.');
    } finally {
      setBookmarkingMessageId(null);
    }
  };

  return (
    <div className="learn-chat-tab">
      {/* Messages */}
      <div className="learn-chat-messages" style={{ fontSize: `${zoom}%` }}>
        {messages.length === 0 && !isSending && (
          <div className="learn-chat-empty">
            <p>Ask about writing craft, techniques, or research topics.</p>
            <p className="learn-chat-empty__hint">
              This conversation is separate from your story development.
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <LearnMessage
            key={msg.id}
            message={msg}
            onBookmark={() => handleBookmark(msg.id)}
            isBookmarking={bookmarkingMessageId === msg.id}
          />
        ))}

        {isSending && (
          <div className="learn-message learn-message--assistant learn-message--loading">
            <div className="learn-message__content">
              <span className="learn-typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="learn-chat-error">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="learn-chat-input">
        <textarea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about writing craft..."
          disabled={isSending}
          rows={2}
        />
        <button
          className="learn-chat-send-btn"
          onClick={handleSend}
          disabled={isSending || !inputValue.trim()}
        >
          {isSending ? '...' : 'Send'}
        </button>
      </div>

      {/* Zoom slider */}
      <div className="learn-chat-zoom">
        <TextSizeStepper
          value={zoom}
          onChange={setZoom}
          min={RESEARCH_CHAT_TEXT_SIZE.min}
          max={RESEARCH_CHAT_TEXT_SIZE.max}
          step={RESEARCH_CHAT_TEXT_SIZE.step}
        />
        <span className="learn-chat-zoom__value">{zoom}%</span>
        {zoom !== 100 && (
          <button
            className="learn-chat-zoom__reset"
            onClick={() => setZoom(100)}
            title="Reset to 100%"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
